import { useEffect, useState } from "react";
import { HeartPulse, Plus, Save, Trash2, RotateCcw, UserCheck, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";

const BULAN = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];
const AUTO = "__AUTO__";

function formatRibuan(v) {
  const digits = String(v ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function parseDigits(v) {
  return String(v ?? "").replace(/\D/g, "");
}

export function KlaimKonfigSection() {
  const [kategori, setKategori] = useState([]);
  const [jabatan, setJabatan] = useState([]);
  const [pegawai, setPegawai] = useState([]);
  const [settings, setSettings] = useState(null);
  const [cutoff, setCutoff] = useState("1");
  const [approverId, setApproverId] = useState(AUTO);
  const [newNama, setNewNama] = useState("");
  // plafon draft: { [kategoriId]: { [jabatanId]: "digits" } }
  const [draft, setDraft] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [savingSetting, setSavingSetting] = useState(false);

  async function load() {
    try {
      const [k, j, p, s] = await Promise.all([
        api.get("/klaim/kategori"),
        api.get("/master/jabatan"),
        api.get("/pegawai"),
        api.get("/settings/klaim"),
      ]);
      setKategori(k.data);
      setJabatan(j.data);
      setPegawai(p.data.filter((u) => u.is_active !== false));
      setSettings(s.data);
      setCutoff(String(s.data.cutoff_bulan));
      setApproverId(s.data.approver_user_id || AUTO);
      const d = {};
      k.data.forEach((kat) => {
        d[kat.id] = {};
        Object.entries(kat.plafon_per_jabatan || {}).forEach(([jid, v]) => {
          d[kat.id][jid] = String(v || "");
        });
      });
      setDraft(d);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }
  useEffect(() => { load(); }, []);

  async function saveSettings() {
    setSavingSetting(true);
    try {
      const { data } = await api.put("/settings/klaim", {
        cutoff_bulan: parseInt(cutoff, 10),
        approver_user_id: approverId === AUTO ? "" : approverId,
      });
      setSettings(data);
      toast.success("Pengaturan klaim disimpan");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingSetting(false);
    }
  }

  async function addKategori() {
    const nama = newNama.trim();
    if (nama.length < 2) {
      toast.error("Nama kategori minimal 2 karakter");
      return;
    }
    try {
      await api.post("/klaim/kategori", { nama, plafon_per_jabatan: {} });
      toast.success(`Kategori "${nama}" ditambahkan — atur plafon per jabatan di bawah`);
      setNewNama("");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function saveKategori(kat) {
    const plafon = {};
    Object.entries(draft[kat.id] || {}).forEach(([jid, digits]) => {
      const v = parseInt(digits || "0", 10);
      if (v > 0) plafon[jid] = v;
    });
    setSavingId(kat.id);
    try {
      await api.put(`/klaim/kategori/${kat.id}`, { nama: kat.nama, plafon_per_jabatan: plafon });
      toast.success(`Plafon "${kat.nama}" disimpan`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingId(null);
    }
  }

  async function removeKategori(kat) {
    if (!window.confirm(`Hapus kategori "${kat.nama}"?`)) return;
    try {
      const { data } = await api.delete(`/klaim/kategori/${kat.id}`);
      if (data.deactivated) toast.info(data.pesan);
      else toast.success(`Kategori "${kat.nama}" dihapus`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function reactivate(kat) {
    try {
      await api.post(`/klaim/kategori/${kat.id}/aktifkan`);
      toast.success(`Kategori "${kat.nama}" diaktifkan kembali`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  function setPlafon(katId, jabId, value) {
    setDraft((d) => ({
      ...d,
      [katId]: { ...(d[katId] || {}), [jabId]: parseDigits(value).slice(0, 12) },
    }));
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="section-klaim">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700 shrink-0">
          <HeartPulse size={18} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-extrabold text-slate-900 leading-tight">
            Klaim Pengobatan — Kategori &amp; Plafon
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Plafon per kategori bersifat <b>per-item (tidak kumulatif)</b> dan dibedakan per <b>Jabatan</b>.
            Saldo direset otomatis setiap awal periode tahunan (Cutoff Tahunan). Kuota terpotong hanya saat klaim <b>Disetujui</b>.
          </p>
        </div>
      </div>

      {/* Cutoff + Approver */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
            <CalendarClock size={12} /> Cutoff Tahunan (bulan awal periode)
          </Label>
          <Select value={cutoff} onValueChange={setCutoff}>
            <SelectTrigger className="mt-1.5" data-testid="input-klaim-cutoff"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BULAN.map((b, i) => (
                <SelectItem key={b} value={String(i + 1)}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
            <UserCheck size={12} /> Approver Default Klaim
          </Label>
          <Select value={approverId} onValueChange={setApproverId}>
            <SelectTrigger className="mt-1.5" data-testid="input-klaim-approver"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={AUTO}>Otomatis — Manager SDM &amp; Umum</SelectItem>
              {pegawai.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} ({u.npp})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {settings?.approver_resolved ? (
            <div className="text-[11px] text-slate-500 mt-1.5" data-testid="klaim-approver-resolved">
              Approver aktif saat ini: <b className="text-teal-800">{settings.approver_resolved.nama}</b> ({settings.approver_resolved.npp})
            </div>
          ) : (
            <div className="text-[11px] text-amber-700 mt-1.5">
              Belum ada Manager SDM &amp; Umum terdeteksi — klaim akan mengikuti rantai atasan pemohon.
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <Button
            onClick={saveSettings}
            disabled={savingSetting}
            className="bg-teal-700 hover:bg-teal-800 gap-1.5"
            data-testid="save-klaim-settings"
          >
            <Save size={13} /> {savingSetting ? "…" : "Simpan Pengaturan"}
          </Button>
        </div>
      </div>

      {/* Tambah kategori */}
      <div className="mt-6 border-t border-slate-100 pt-5">
        <Label className="text-xs font-bold text-slate-600">Tambah Kategori Pengobatan</Label>
        <div className="flex gap-2 mt-1.5 max-w-md">
          <Input
            placeholder="Contoh: Kacamata, Persalinan…"
            value={newNama}
            onChange={(e) => setNewNama(e.target.value)}
            data-testid="input-klaim-kategori-baru"
          />
          <Button onClick={addKategori} className="bg-teal-700 hover:bg-teal-800 gap-1.5 shrink-0" data-testid="add-klaim-kategori-btn">
            <Plus size={13} /> Tambah
          </Button>
        </div>
      </div>

      {/* Daftar kategori + plafon per jabatan */}
      <div className="mt-5 space-y-4">
        {kategori.length === 0 ? (
          <div className="text-sm text-slate-500 italic">Belum ada kategori pengobatan.</div>
        ) : (
          kategori.map((kat) => (
            <div
              key={kat.id}
              className={`rounded-lg border p-4 ${kat.is_active === false ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`}
              data-testid={`klaim-kategori-row-${kat.id}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900">{kat.nama}</span>
                  {kat.is_active === false ? (
                    <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                      Nonaktif
                    </span>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  {kat.is_active === false ? (
                    <Button size="sm" variant="outline" onClick={() => reactivate(kat)} className="gap-1.5" data-testid={`klaim-aktifkan-${kat.id}`}>
                      <RotateCcw size={12} /> Aktifkan
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        onClick={() => saveKategori(kat)}
                        disabled={savingId === kat.id}
                        className="bg-teal-700 hover:bg-teal-800 gap-1.5"
                        data-testid={`klaim-simpan-plafon-${kat.id}`}
                      >
                        <Save size={12} /> {savingId === kat.id ? "…" : "Simpan Plafon"}
                      </Button>
                      {/* Tombol Hapus dihilangkan (Keamanan Data — Feb 2026). */}
                    </>
                  )}
                </div>
              </div>
              {kat.is_active !== false ? (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {jabatan.map((j) => (
                    <div key={j.id}>
                      <Label className="text-[11px] font-semibold text-slate-500">{j.nama}</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">Rp</span>
                        <Input
                          inputMode="numeric"
                          className="pl-8 h-9 text-sm font-mono-clock"
                          placeholder="0 (tidak diatur)"
                          value={formatRibuan(draft[kat.id]?.[j.id])}
                          onChange={(e) => setPlafon(kat.id, j.id, e.target.value)}
                          data-testid={`plafon-${kat.id}-${j.id}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default KlaimKonfigSection;
